import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  formatCompetitorsOfTotal,
  formatInvestmentMultiple,
  formatPercent,
  type PersonaOpportunityDisplayRow,
} from "../../../lib/persona-opportunity-row-model.js";
import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";

export interface PersonaOpportunityTableProps {
  rows: PersonaOpportunityDisplayRow[];
  selectedIds?: ReadonlySet<string>;
  selectionEnabled?: boolean;
  onToggleSelect?: (opportunityId: string) => void;
}

const TH =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
const TD = "px-3 py-3 align-middle text-[13px] text-foreground";

/** Wraps long cell copy so the full string is reachable via hover tooltip. */
function ExpandableCellText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{text}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-normal break-words text-left">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function DrivenFeatureCell({
  text,
  isDriven,
  drivenLabel,
}: {
  text: string;
  isDriven: boolean;
  drivenLabel: string;
}) {
  return (
    <div className="space-y-1">
      {isDriven ? (
        <span className="inline-flex rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          {drivenLabel}
        </span>
      ) : null}
      <ExpandableCellText
        text={text}
        className={cn(
          "line-clamp-3 whitespace-normal break-words",
          isDriven && "font-semibold text-foreground",
        )}
      />
    </div>
  );
}

export function PersonaOpportunityTable({
  rows,
  selectedIds,
  selectionEnabled = false,
  onToggleSelect,
}: PersonaOpportunityTableProps) {
  const { t } = useTranslation();
  const drivenLabel = t("personaOpportunities.active.drivenByBadge", "Driving");
  const valueUnavailable = t("personaOpportunities.active.valueUnavailable", "—");

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-x-auto rounded-b-md border border-t-0 border-border">
        <table className="w-full min-w-[1900px] border-collapse">
          <thead className="bg-muted/30">
            <tr className="border-b border-border">
              <th className={`${TH} w-10`} scope="col">
                <span className="sr-only">{t("personaOpportunities.active.selectColumn", "Select")}</span>
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.status", "Status")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.video", "Video")}
              </th>
              <th className={`${TH} min-w-[200px]`} scope="col">
                {t("personaOpportunities.active.columns.painPoint", "Pain point")}
              </th>
              <th className={`${TH} min-w-[200px]`} scope="col">
                {t("personaOpportunities.active.columns.motivation", "Motivation")}
              </th>
              <th className={`${TH} min-w-[220px]`} scope="col">
                {t("personaOpportunities.active.columns.angle", "Angle")}
              </th>
              <th className={`${TH} min-w-[280px]`} scope="col">
                {t("personaOpportunities.active.columns.why", "Why")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.creatives", "Creatives")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.competitors", "Competitors")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.stickRate", "Stick rate")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.duplicated", "Duplicated")}
              </th>
              <th className={TH} scope="col">
                {t("personaOpportunities.active.columns.investmentIndex", "Investment index")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const investment = formatInvestmentMultiple(row.investmentIndex);
              const painText = row.painPoint ?? valueUnavailable;
              const motivationText = row.motivation ?? valueUnavailable;
              return (
                <tr
                  key={row.id}
                  className="border-b border-border/70 transition-colors hover:bg-muted/40"
                >
                  <td className={TD}>
                    <PersonaOpportunitySelectCheckbox
                      checked={selectedIds?.has(row.id) ?? false}
                      disabled={!selectionEnabled}
                      onToggle={() => onToggleSelect?.(row.id)}
                    />
                  </td>
                  <td className={TD}>
                    <span className="inline-flex rounded-sm border border-input bg-background px-1.5 py-0.5 text-[11px] font-medium">
                      {personaOpportunityStatusLabel(t, row.status)}
                    </span>
                  </td>
                  <td className={TD}>
                    <PersonaOpportunitySupportingMedia row={row} variant="table" />
                  </td>
                  <td
                    className={cn(
                      `${TD} max-w-[240px] text-muted-foreground`,
                      row.drivenBy === "painpoint" && "bg-primary/5",
                    )}
                  >
                    <DrivenFeatureCell
                      text={painText}
                      isDriven={row.drivenBy === "painpoint"}
                      drivenLabel={drivenLabel}
                    />
                  </td>
                  <td
                    className={cn(
                      `${TD} max-w-[240px] text-muted-foreground`,
                      row.drivenBy === "motivation" && "bg-primary/5",
                    )}
                  >
                    <DrivenFeatureCell
                      text={motivationText}
                      isDriven={row.drivenBy === "motivation"}
                      drivenLabel={drivenLabel}
                    />
                  </td>
                  <td className={`${TD} max-w-[280px] font-semibold`}>
                    <ExpandableCellText
                      text={row.angle}
                      className="line-clamp-3 whitespace-normal break-words"
                    />
                  </td>
                  <td className={`${TD} max-w-[360px] text-muted-foreground`}>
                    <ExpandableCellText
                      text={row.why}
                      className="line-clamp-3 whitespace-normal break-words"
                    />
                  </td>
                  <td className={`${TD} tabular-nums`}>{row.creativesCount}</td>
                  <td className={`${TD} tabular-nums`}>
                    {formatCompetitorsOfTotal(row.competitorsRunningCount, row.competitorsTotal)}
                  </td>
                  <td className={`${TD} tabular-nums`}>{formatPercent(row.stickRatePct)}</td>
                  <td className={`${TD} tabular-nums`}>{formatPercent(row.duplicatedPct)}</td>
                  <td className={TD}>
                    <div className="space-y-0.5">
                      <div className="text-sm font-semibold text-primary">
                        {investment ?? valueUnavailable}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {t("personaOpportunities.active.youShare", {
                          pct: formatPercent(row.myInvestmentSharePct),
                          defaultValue: `You ${formatPercent(row.myInvestmentSharePct)}`,
                        })}
                        {" · "}
                        {t("personaOpportunities.active.marketShare", {
                          pct: formatPercent(row.marketInvestmentSharePct),
                          defaultValue: `Market ${formatPercent(row.marketInvestmentSharePct)}`,
                        })}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}
